import { getLocale } from '@sonnen/web-utils';
import clsx from 'clsx';
import { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAppContext } from '../AppContext/AppContext';
import { AuthGuard } from '../components/AuthGuard/AuthGuard';
import { DataSafeguard } from '../components/DataSafeguard/DataSafeguard';
import Footer from '../components/Footer/Footer';
import { Logout } from '../components/Logout/Logout';
import MobileCta from '../components/MobileCta/MobileCta';
import { SonnenHomeEnergyLogo } from '../components/SonnenHomeEnergyLogo/SonnenHomeEnergyLogo';
import { allRoutes, AppRoute } from '../constants';
import { useCurrentRoute } from '../hooks/useCurrentRoute';
import { useCurrentUserData } from '../hooks/useCurrentUserData';
import { BatteryOptimization } from '../Pages/BatteryOptimization/BatteryOptimization';
import { Confirmation } from '../Pages/Confirmation/Confirmation';
import DataEntry from '../Pages/DataEntry/DataEntry';
import DataReview from '../Pages/DataReview/DataReview';
import EnergyStore from '../Pages/EnergyStore/EnergyStore';
import Home from '../Pages/Home/Home';
import { Login } from '../Pages/Login/Login';
import MaintenanceNotice from '../Pages/MaintenanceNotice/MaintenanceNotice';
import PageNotFound from '../Pages/PageNotFound/PageNotFound';
import { SelfConsumptionPlus } from '../Pages/SelfConsumptionPlus/SelfConsumptionPlus';
import TariffOverview from '../Pages/TariffOverview/TariffOverview';
import TariffsComparison from '../Pages/TariffsComparison/TariffsComparison';
import { PageBackground } from '../types';
import styles from './AppRoutes.module.css';
import TopbarWithNameAndLogout from './TopbarWithNameAndLogout/TopbarWithNameAndLogout';

interface AuthProtectedRoute {
  path: AppRoute;
  element: React.ReactElement;
  allowVisitor: boolean;
}

// Route protection configuration
export const authProtectedRoutes: AuthProtectedRoute[] = [
  { path: AppRoute.SELF_CONSUMPTION_PLUS, element: <SelfConsumptionPlus />, allowVisitor: false },
  { path: AppRoute.TARIFFS_COMPARISON, element: <TariffsComparison />, allowVisitor: true },
  { path: AppRoute.TARIFF_OVERVIEW, element: <TariffOverview />, allowVisitor: true },
  { path: AppRoute.BATTERY_OPTIMIZATION, element: <BatteryOptimization />, allowVisitor: false },
  { path: AppRoute.ENERGY_STORE, element: <EnergyStore />, allowVisitor: false },
];

const renderDatasafe = (element: React.ReactNode) => {
  return <DataSafeguard>{element}</DataSafeguard>;
};

const stylePerBackground: Record<PageBackground, string> = {
  [PageBackground.HOME]: styles.homePageBackground,
  [PageBackground.CONFIRMATION]: styles.confirmationPageBackground,
  [PageBackground.CONTRACT_CREATION_BLOCKED]: styles.contractCreationBlockedBackground,
};

export const AppRoutes = () => {
  const [userData, userDataLoading] = useCurrentUserData();
  const currentRoute = useCurrentRoute();
  const { currentBackground } = useAppContext();
  const locale = getLocale();

  const routesWithLogo: AppRoute[] = allRoutes.filter(
    (route) =>
      route !== AppRoute.ROOT &&
      route !== AppRoute.LOGIN &&
      route !== AppRoute.MAINTENANCE &&
      route !== AppRoute.ORDER_COMPLETE,
  );
  const visibleSonnenHomeLogo = routesWithLogo.includes(currentRoute);

  const backgroundStyles = useMemo(() => {
    if (!currentBackground || !locale) return '';
    const localeStyle = styles[locale.substring(0, 2)];
    return clsx(stylePerBackground[currentBackground], localeStyle);
  }, [currentBackground, locale]);

  const authRoutes = useMemo(
    () =>
      authProtectedRoutes.map(({ path, element, allowVisitor }) => (
        <Route
          key={path}
          path={path}
          element={<AuthGuard allowVisitor={allowVisitor}>{element}</AuthGuard>}
        />
      )),
    [],
  );

  const getRootComponent = () => {
    if (userDataLoading) return <Login />;
    if (userData) {
      if (userData.featureFlags?.allowToAccessEnergyStore) {
        return <EnergyStore />;
      } else {
        return <Navigate to={AppRoute.TARIFFS_COMPARISON} />;
      }
    }
    return <Home />;
  };

  return (
    <div data-testid="app-routes" className={backgroundStyles}>
      <TopbarWithNameAndLogout />

      {visibleSonnenHomeLogo && <SonnenHomeEnergyLogo />}

      <main>
        <Routes>
          <Route path={AppRoute.ROOT} element={getRootComponent()} />
          <Route path={AppRoute.MAINTENANCE} element={<MaintenanceNotice />} />
          <Route path={AppRoute.LOGIN} element={<Login />} />
          <Route path={AppRoute.LOGOUT} element={<Logout />} />
          {authRoutes}
          <Route path={AppRoute.DATA_ENTRY} element={renderDatasafe(<DataEntry />)} />
          <Route path={AppRoute.DATA_REVIEW} element={renderDatasafe(<DataReview />)} />
          <Route path={AppRoute.ORDER_COMPLETE} element={renderDatasafe(<Confirmation />)} />

          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </main>

      <Footer />

      <MobileCta />
    </div>
  );
};
